// services/accountDomainErrorMessages.js
//
// Copy de usuario para los `code` de organizaciones, membresías, invitaciones
// y proyectos.
//
// El `message` que manda el backend es fijo y neutro —nunca el texto de la
// excepción, que filtraría detalle interno—, así que la frase la decide el
// frontend. Se ramifica sobre el `code` y no sobre ese texto: es lo estable, y
// buscar substrings en el mensaje ya dejó una rama muerta en
// `OrganizationMembers.jsx` cuando el backend lo reescribió.
//
// En inglés y fuera de i18n siguiendo a `databaseErrorMessages.js`, el mapa
// equivalente del servicio de base de datos; esta superficie todavía rotula
// todo en inglés. La otra vía —`billingErrors` en `i18n/i18n.js`, traducción al
// español incluida— se escribió y nunca se cableó: cero consumidores. Cuando
// esta superficie se traduzca, las llaves de este mapa ya son llaves de i18n
// válidas; migrar es cambiar el resolver, no la tabla.

// `LAST_OWNER_PROTECTED` cubre tres intentos de dejar la organización sin dueño
// y la salida del usuario cambia en cada uno; el backend los separa con
// `context.action`, no con códigos distintos.
//
// `downgrade_self` hoy no llega: al bajarse uno mismo, el guard genérico de
// `update_org_member.py` lanza primero con `action: "downgrade"`. Se mapea
// igual porque el backend lo declara, y mientras tanto el copy de `downgrade`
// cubre el caso.
const LAST_OWNER = {
  remove:
    'You can\'t remove the last owner. Transfer ownership to someone else first.',
  downgrade:
    'You can\'t change the last owner\'s role. Promote someone else to owner first.',
  downgrade_self:
    'You\'re the last owner. Promote someone else to owner before changing your own role.',
  fallback: 'An organization must keep at least one owner.',
};

// Un código, una frase. Sin interpolar: los context traen UUIDs, que no le
// dicen nada al usuario, y el email ya está en el campo que acaba de llenar.
//
// Un código que sirve a dos pantallas lleva copy neutro de entidad —ver
// `RESOURCE_CONFLICT`, que emiten organizaciones y proyectos— antes que abrir
// una dimensión de pantalla en la tabla. Si a una pantalla no le alcanza, el
// override va en su sitio de llamada.
const MESSAGES = {
  // Conflictos de estado (409).
  ORGANIZATION_NOT_ACTIVE: 'This organization is closed, so it can\'t be modified.',
  ALREADY_ORGANIZATION_MEMBER: 'That user already belongs to this organization.',
  MEMBERSHIP_NOT_ACTIVE: 'That membership is no longer active in this organization.',
  ORGANIZATION_ALREADY_CLOSED: 'This organization is already closed.',
  PENDING_INVITATION_EXISTS:
    'There\'s already a pending invitation for that email. Cancel it before sending a new one.',
  INVITATION_EXPIRED:
    'This invitation expired. Ask an organization admin to send you a new one.',
  INVITATION_EMAIL_MISMATCH:
    'This invitation was issued for a different email. Sign in with the address it was sent to.',
  PROJECT_ALREADY_SUSPENDED: 'This project is already suspended.',
  PROJECT_SOFT_DELETED: 'This project was deleted, so it can\'t be changed.',
  RESOURCE_CONFLICT: 'That name is already taken. Choose a different one.',

  // Entrada inválida (422).
  EMAIL_NOT_REGISTERED:
    'No account is registered with this email. Only users who already have an account can be added directly — invite them instead.',
  PERSONAL_PROJECT_ACCOUNT_REQUIRED:
    'A personal project needs an account. Pick one and try again.',
  PROJECT_ACCOUNT_ORGANIZATION_MISMATCH: 'That account doesn\'t belong to this organization.',
  PROJECT_ACCOUNT_NOT_OWNED: 'You don\'t have access to that account.',

  // Un valor puede ser función del `context` cuando un código tiene más de una
  // salida. El resolver no distingue: es la misma tabla.
  LAST_OWNER_PROTECTED: (context) => LAST_OWNER[context?.action] ?? LAST_OWNER.fallback,
};

/**
 * Resuelve el mensaje de un error del dominio de cuentas, devolviendo el
 * `fallback` cuando el código no está mapeado para no ocultar un error que
 * todavía no tiene copy propio.
 *
 * Úsala solo si necesitas componer el fallback tú (p. ej. `${status}: ${msg}`);
 * si no, `messageForResult` ya arma la cadena completa.
 *
 * @param {{code?: string|null, context?: object|null, fallback?: string|null}} params
 * @returns {string|null}
 */
export function resolveAccountDomainError({ code, context, fallback } = {}) {
  if (!code) return fallback ?? null;
  const entry = MESSAGES[code];
  if (entry === undefined) return fallback ?? null;
  return typeof entry === 'function' ? entry(context) : entry;
}

/**
 * Igual que `resolveAccountDomainError`, tomando el sobre que devuelven
 * `OrganizationService` y `ProjectService` tal cual, y cayendo al rótulo de la
 * acción cuando no hay nada mejor que decir.
 *
 * @param {{code?: string|null, context?: object|null, error?: string[]|string|null}|null} result
 * @param {string} fallback - Rótulo de la acción, p. ej. 'Failed to add member'
 * @returns {string}
 */
export function messageForResult(result, fallback) {
  const detail = Array.isArray(result?.error) ? result.error.join(', ') : result?.error;
  return (
    resolveAccountDomainError({
      code: result?.code,
      context: result?.context,
      fallback: detail,
    }) || fallback
  );
}
